import { v4 as uuidv4 } from 'uuid';
import { Column, DataType, ColumnFilter, FilterType, NumberFilter, StringFilter } from '../bootgridTypes';
import { AdvancedFilterTemplates } from './advancedFilterTemplates';
import { luscinia } from '../../luscinia';

export class FilterComponent {
    readonly id: string = "filter-" + uuidv4();
    private container: HTMLElement;
    private filterSelectContainer: HTMLElement;
    private filterInputContainer: HTMLElement;
    private deleteButton: HTMLButtonElement;

    columnSelectElement: HTMLSelectElement;
    filterSelectElement: HTMLSelectElement;
    valueInputElements: NodeListOf<HTMLInputElement | HTMLSelectElement>;

    private filterSelectChangeHandler: (e: Event) => void;
    private columnSelectChangeHandler: (e: Event) => void;
    private deleteHandler: (e: Event) => void;
    private columns: Column[];
    private filterComponentState: FilterComponentState;

    constructor(container: HTMLElement, config: FilterComponentConfig) {
        this.container = container;
        this.columns = config.columns;
        this.filterComponentState = {
            selectedColumnId: null,
            selectedFilter: null,
            filterValues: null,
        };

        if (config.filterComponentState) {
            this.filterComponentState = config.filterComponentState;
        }

        this.columnSelectChangeHandler = (e: Event) => {
            this.filterComponentState.selectedColumnId = this.columnSelectElement.value;
            this.filterComponentState.selectedFilter = null;
            this.filterComponentState.filterValues = null;
            this.renderFilterSelect();
            this.container.dispatchEvent(new Event('filter-component-stateChanged'));
        };
        this.filterSelectChangeHandler = (e: Event) => {
            this.filterComponentState.selectedFilter = this.filterSelectElement.value as NumberFilter | StringFilter;
            this.filterComponentState.filterValues = null;
            this.renderInputElement();
            this.container.dispatchEvent(new Event('filter-component-stateChanged'));
        };
        this.deleteHandler = (e: Event) => {
            this.container.remove();
            config.onRemove(e);
            this.container.dispatchEvent(new Event('filter-component-stateChanged'));
        };
        this.container.addEventListener('filter-component-stateChanged', config.onStateChange);
    }
    render() {
        this.container.innerHTML = '';
        this.constructFilterComponent(this.container, this.id);

        // Get references to elements
        this.filterSelectContainer = this.container.querySelector('.filter-select-container') as HTMLElement;
        this.columnSelectElement = this.container.querySelector('.column-select') as HTMLSelectElement;
        this.filterInputContainer = this.container.querySelector('.filter-input-container') as HTMLElement;
        this.deleteButton = this.container.querySelector('.delete-button') as HTMLButtonElement;

        // Add event listeners
        this.columnSelectElement.addEventListener('change', this.columnSelectChangeHandler);
        this.deleteButton.addEventListener('click', this.deleteHandler);
        
        this.columnSelectElement.innerHTML = '';
        this.constructOption(this.columnSelectElement, 'none', '--Select a column--');

        this.columns.forEach((column) => {
            if (this.isValidDataType(column.dataType)) {
                const option = document.createElement('option');
                option.value = column.id;
                option.text = column.text;
                option.setAttribute('data-data-type', column.dataType);
                if (column.filterWidget) option.setAttribute('data-filter-widget', column.filterWidget);
                if (column.filterOptionsUrl) option.setAttribute('data-filter-options-url', column.filterOptionsUrl);
                this.columnSelectElement.append(option);
            }
        });

        if (this.filterComponentState?.selectedColumnId) {
            this.columnSelectElement.value = this.filterComponentState.selectedColumnId;
            this.renderFilterSelect();
        }
    }
    getFilter(): ColumnFilter {
        return {
            ColumnName: this.columnSelectElement.value === 'none' ? null : this.columnSelectElement.value,
            DataType: this.getSelectedColumnDataType(),
            FilterValue: this.getSelectedFilter(),
            Values: this.getValues(),
        };
    }
    getValues(): string[] {
        if ((this.valueInputElements === undefined || this.valueInputElements === null) && this.getSelectedFilter() !== 'empty') {
            return null;
        }

        let values = null;
        if (this.valueInputElements !== undefined && this.valueInputElements !== null) {
            // Checkboxen (bv. de checkbox-multiselect widget) alleen meenemen als ze aangevinkt zijn
            values = Array.from(this.valueInputElements)
                .filter((input) => !(input instanceof HTMLInputElement) || input.type !== 'checkbox' || input.checked)
                .map((input) => input.value);
        }
        return values;
    }
    private constructFilterComponent(container: HTMLElement, filterId: string) {
        const filterContainer = document.createElement('div');
        filterContainer.classList.add(filterId, "filter", "d-flex", "gap-2");

        this.constructDeleteButton(filterContainer);
        this.constructColumnSelect(filterContainer);
        this.constructFilterSelectContainer(filterContainer);
        this.constructFilterInputContainer(filterContainer);

        container.appendChild(filterContainer);
    }
    private constructFilterSelectContainer(container: HTMLDivElement) {
        const filterSelectContainer = document.createElement('div');
        filterSelectContainer.classList.add('form-group', 'filter-select-container', 'w-25');        

        container.appendChild(filterSelectContainer);
    }        
    private constructFilterInputContainer(container: HTMLDivElement) {
        const filterInputContainer = document.createElement('div');
        filterInputContainer.classList.add('form-group', 'filter-input-container', 'w-75');
        //filterInputContainer.style.display = 'flex';
        //filterInputContainer.style.alignItems = 'center';
        //filterInputContainer.style.marginLeft = '10px';

        container.appendChild(filterInputContainer);
    }
    private constructColumnSelect(container: HTMLElement) {
        const select = document.createElement('select');
        select.classList.add('form-control', 'column-select', 'w-25');
        this.constructOption(select, 'Empty', '--Select a column--');
        container.appendChild(select);
    }
    private constructDeleteButton(container: HTMLDivElement) {
        const deleteButton = document.createElement('button');
        deleteButton.classList.add('btn', 'btn-outline-danger', 'delete-button');
        deleteButton.setAttribute('type', 'button');

        const span = document.createElement('i');
        span.classList.add('fa', 'fa-trash');

        deleteButton.appendChild(span);
        container.appendChild(deleteButton);
    }
    private renderFilterSelect() {
        this.filterSelectContainer.innerHTML = '';
        this.filterInputContainer.innerHTML = '';

        if (this.getSelectedColumnFilterWidget() === 'checkbox-multiselect') {
            // Vaste operator (any-of), geen operator-keuze nodig - toon direct de checkbox-lijst
            this.filterComponentState.selectedFilter = 'includes_any';
            const input = this.constructCheckboxMultiSelectInput(this.getSelectedColumnFilterOptionsUrl());
            this.filterInputContainer.appendChild(input);
            this.valueInputElements = this.filterInputContainer.querySelectorAll('input, select') as NodeListOf<HTMLInputElement | HTMLSelectElement>;
            input.addEventListener('input-change', () => {
                this.container.dispatchEvent(new Event('filter-component-stateChanged'));
                this.filterComponentState.filterValues = this.getValues();
            });
            return;
        }

        const select = document.createElement('select');
        select.classList.add('form-control', 'filter-select');
        this.constructOption(select, 'none', '--Select a filter--');
        this.filterSelectContainer.appendChild(select);
    
        this.filterSelectElement = this.filterSelectContainer.querySelector('.filter-select') as HTMLSelectElement;
        this.filterSelectElement.addEventListener('change', this.filterSelectChangeHandler);
        this.renderFilterSelectOptions();
        
        if (this.filterComponentState?.selectedFilter) {
            this.filterSelectElement.value = this.filterComponentState.selectedFilter;
            this.renderInputElement();
        }
    }
    private renderInputElement() {
        this.filterInputContainer.innerHTML = '';
        if (this.filterSelectElement.value === 'empty') {
            return;
        }
        const filterType = this.getFilterType();
        if (this.filterSelectElement.value === 'between' || this.filterSelectElement.value === 'not_between') {
            this.renderBetweenInput(filterType);
        } else {
            this.renderSingleInput(filterType);
        }
        this.valueInputElements = this.filterInputContainer.querySelectorAll('input, select') as NodeListOf<HTMLInputElement | HTMLSelectElement>;
        if (this.filterComponentState?.filterValues) {
            this.valueInputElements.forEach((input, index) => {
                input.value = this.filterComponentState.filterValues[index];
            });
        }
    }
    private renderFilterSelectOptions() {
        this.filterSelectElement.innerHTML = '';
        this.constructOption(this.filterSelectElement, 'Empty', '--Select a filter--');
        
        const filterType = this.getFilterType();
        switch (filterType) {
            case FilterType.number:
            case FilterType.date:
                this.filterSelectElement.insertAdjacentHTML('beforeend', AdvancedFilterTemplates.getNumericOptions(luscinia.config.culture));
                break;
            case FilterType.string:
                this.filterSelectElement.insertAdjacentHTML('beforeend', AdvancedFilterTemplates.getStringOptions(luscinia.config.culture));
                break;
            case FilterType.boolean:
                this.filterSelectElement.insertAdjacentHTML('beforeend', AdvancedFilterTemplates.getBooleanOptions(luscinia.config.culture));
                break;
            default:
                break;
        }
    }
    private getFilterType(): FilterType {
        switch (this.getSelectedColumnDataType()) {
            case 'Date':
            case 'Time':
            case 'DateTime':
            case 'DateTimeOffset':
            case 'DateOnly':
                return FilterType.date;
            case 'Decimal':
            case 'Int32':
                return FilterType.number;
            case 'String':
            case 'Guid':
                return FilterType.string;
            case 'Boolean':
                return FilterType.boolean;
            default:
                break;
        }
    }
    private getSelectedColumnDataType(): DataType {
        const selectedOption = this.columnSelectElement.selectedOptions[0];
        if (selectedOption.value === 'none') {
            return null;
        }
        const dataType = selectedOption.getAttribute('data-data-type') as DataType;
        if (!this.isValidDataType(dataType)) {
            throw new Error(`Invalid data type: ${dataType}`);
        }
        return dataType;
    }
    private getSelectedFilter(): NumberFilter | StringFilter {
        if (this.getSelectedColumnFilterWidget() === 'checkbox-multiselect') {
            return 'includes_any';
        }
        if (!this.filterSelectElement) return null;
        return this.filterSelectElement.value === 'Empty' ? null : this.filterSelectElement.value as NumberFilter | StringFilter;
    }
    private getSelectedColumnFilterWidget(): string {
        const selectedOption = this.columnSelectElement?.selectedOptions[0];
        if (!selectedOption || selectedOption.value === 'none') return null;
        return selectedOption.getAttribute('data-filter-widget');
    }
    private getSelectedColumnFilterOptionsUrl(): string {
        const selectedOption = this.columnSelectElement?.selectedOptions[0];
        if (!selectedOption || selectedOption.value === 'none') return null;
        return selectedOption.getAttribute('data-filter-options-url');
    }
    private constructCheckboxMultiSelectInput(optionsUrl: string): HTMLElement {
        const container = document.createElement('div');
        container.classList.add('form-control', 'filter-checkbox-multiselect');
        container.style.maxHeight = '160px';
        container.style.overflowY = 'auto';
        container.innerHTML = '<div class="text-muted small p-1">Laden...</div>';

        if (!optionsUrl) {
            container.innerHTML = '<div class="text-danger small p-1">Geen opties-url geconfigureerd</div>';
            return container;
        }

        const addOptionRow = (optionValue: string, labelText: string) => {
            const wrap = document.createElement('div');
            wrap.classList.add('form-check');

            const checkboxId = 'filter-cb-' + uuidv4();
            const input = document.createElement('input');
            input.type = 'checkbox';
            input.classList.add('form-check-input');
            input.value = optionValue;
            input.id = checkboxId;
            if (this.filterComponentState?.filterValues?.includes(optionValue)) {
                input.checked = true;
            }
            input.addEventListener('change', () => {
                container.dispatchEvent(new Event('input-change'));
            });

            const label = document.createElement('label');
            label.classList.add('form-check-label');
            label.setAttribute('for', checkboxId);
            label.textContent = labelText;

            wrap.appendChild(input);
            wrap.appendChild(label);
            container.appendChild(wrap);
        };

        fetch(optionsUrl).then((r) => r.json()).then((options: string[]) => {
            container.innerHTML = '';
            // "(leeg)" staat altijd bovenaan - filtert op rijen zonder waarde in deze kolom
            addOptionRow('', '(leeg)');
            options.forEach((optionValue) => addOptionRow(optionValue, optionValue));
            // Elementen zijn async bijgekomen - opnieuw ophalen zodat getValues()/getFilter() ze meeneemt
            this.valueInputElements = this.filterInputContainer.querySelectorAll('input, select') as NodeListOf<HTMLInputElement | HTMLSelectElement>;
        }).catch(() => {
            container.innerHTML = '<div class="text-danger small p-1">Kon opties niet laden</div>';
        });

        return container;
    }
    private renderSingleInput(filterType: FilterType) {
        let input: HTMLElement;

        switch (filterType) {
            case FilterType.date:
                input = this.constructDateInput();
                break;
            case FilterType.number:
                input = this.constructNumberInput();
                break;
            case FilterType.string:
                input = this.constructStringInput();
                break;
            case FilterType.boolean:
                input = this.constructBooleanInput();
                break;
            default:
                throw new Error(`Unknown filterType: ${filterType}`);
        }

        input.addEventListener('input-change', () => {
            this.container.dispatchEvent(new Event('filter-component-stateChanged'));
            this.filterComponentState.filterValues = Array.from(this.valueInputElements).map((input) => input.value);
        });

        this.filterInputContainer.appendChild(input);
    }
    private renderBetweenInput(filterType: FilterType) {
        let input1: HTMLElement;
        let input2: HTMLElement;
        
        const spanEl = document.createElement('span');
        spanEl.innerHTML = '-';
        spanEl.style.margin = '0 5px';

        switch (filterType) {
            case FilterType.date:
                input1 = this.constructDateInput();
                input2 = this.constructDateInput();
                break;
            case FilterType.number:
                input1 = this.constructNumberInput();
                input2 = this.constructNumberInput();
                break;
            case FilterType.string:
                input1 = this.constructStringInput();
                input2 = this.constructStringInput();
                break;
            default:
                throw new Error(`Unknown filterType: ${filterType}`);
        }
        
        input1.addEventListener('input-change', () => {
            this.container.dispatchEvent(new Event('filter-component-stateChanged'));
            this.filterComponentState.filterValues = Array.from(this.valueInputElements).map((input) => input.value);
        });

        input2.addEventListener('input-change', () => {
            this.container.dispatchEvent(new Event('filter-component-stateChanged'));
            this.filterComponentState.filterValues = Array.from(this.valueInputElements).map((input) => input.value);
        });

        this.filterInputContainer.appendChild(input1);
        this.filterInputContainer.appendChild(spanEl);
        this.filterInputContainer.appendChild(input2);
    }
    private constructDateInput(): HTMLElement {
        const dateInputElement = document.createElement('div');
        dateInputElement.setAttribute('class', 'form-group');

        const inputGroupElement = document.createElement('div');
        inputGroupElement.setAttribute('class', 'input-group date');
        inputGroupElement.setAttribute('id', 'datePicker-' + uuidv4());

        const inputElement = document.createElement('input');
        inputElement.setAttribute('type', 'text');
        inputElement.setAttribute('class', 'form-control');

        const inputGroupAddonElement = document.createElement('span');
        inputGroupAddonElement.setAttribute('class', 'input-group-addon');

        const glyphiconElement = document.createElement('span');
        glyphiconElement.setAttribute('class', 'glyphicon glyphicon-calendar');

        inputGroupAddonElement.appendChild(glyphiconElement);
        inputGroupElement.appendChild(inputElement);
        inputGroupElement.appendChild(inputGroupAddonElement);
        dateInputElement.appendChild(inputGroupElement);

        ($(inputGroupElement) as any).datetimepicker({
            locale: luscinia.config.culture,
            format: 'DD-MM-YYYY',
        }).on('dp.change', () => {
            dateInputElement.dispatchEvent(new Event('input-change'));
        });

        return dateInputElement;
    }
    private constructNumberInput(): HTMLInputElement {
        const inputElement = document.createElement('input');
        inputElement.setAttribute('type', 'number');
        inputElement.setAttribute('class', 'form-control');
        inputElement.setAttribute('placeholder', 'Enter value');

        inputElement.addEventListener('input', () => {
            inputElement.dispatchEvent(new Event('input-change'));
        });

        return inputElement;
    }
    private constructStringInput(): HTMLInputElement {
        const inputElement = document.createElement('input');
        inputElement.setAttribute('type', 'text');
        inputElement.classList.add('form-control');
        inputElement.setAttribute('placeholder', 'Enter value');

        inputElement.addEventListener('input', () => {
            inputElement.dispatchEvent(new Event('input-change'));
        });

        return inputElement;
    }
    private constructBooleanInput(): HTMLElement {
        const select = document.createElement('select');
        select.classList.add('form-control');
        this.constructOption(select, 'Empty', '--Select a value--');
        this.constructOption(select, 'true', 'True');
        this.constructOption(select, 'false', 'False');

        select.addEventListener('change', () => {
            select.dispatchEvent(new Event('input-change'));
        });

        return select;        
    }
    private isValidDataType(value: string): boolean {
        const validDataTypes: DataType[] = ['Date', 'Time', 'DateTime', 'DateTimeOffset', 'DateOnly', 'Decimal', 'Int32', 'String', 'Guid', 'Empty', 'Boolean'];
        return validDataTypes.includes(value as DataType);
    }
    private constructOption(container: HTMLElement, value: string, text: string) {
        const option = document.createElement('option');
        if (value === 'Empty') {
            option.setAttribute('data-data-type', 'Empty'); 
        }
        option.value = value;
        option.text = text;
    
        container.appendChild(option);
    }    
}

export interface FilterComponentState {
    selectedColumnId: string;
    selectedFilter: NumberFilter | StringFilter;
    filterValues: string[];
}

export interface FilterComponentConfig {
    onStateChange: (e: Event) => void;
    onRemove: (e: Event) => void;
    columns: Column[];
    filterComponentState?: FilterComponentState;
}